if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.imageMap = {}; // Store images by model code

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            // Setup image filtering after content loads
            setTimeout(() => {
              this.setupImageFiltering();
            }, 200);

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      // Build image map from main gallery
      buildImageMap() {
        console.log('📊 Building image map from main gallery...');
        this.imageMap = {};

        // Get images from main product gallery
        const mainGalleryImages = document.querySelectorAll('media-gallery [data-model-code]');

        mainGalleryImages.forEach((element) => {
          const modelCode = element.getAttribute('data-model-code');
          const img = element.querySelector('img');

          if (modelCode && img) {
            this.imageMap[modelCode] = {
              src: img.src,
              srcset: img.srcset || '',
              alt: img.alt || '',
            };
          }
        });

        console.log('✅ Image map built with model codes:', Object.keys(this.imageMap));
      }

      // Setup image filtering for modal
      setupImageFiltering() {
        console.log('🔧 Setting up modal image filtering...');

        // Build image map first
        this.buildImageMap();

        if (Object.keys(this.imageMap).length === 0) {
          console.log('❌ No images found in main gallery - filtering disabled');
          return;
        }

        // Find all possible variant selectors
        const selectors = this.modalContent.querySelectorAll(
          'select[name*="option"], input[name*="option"], input[type="radio"], .variant-input, variant-selects'
        );

        console.log(`📋 Found ${selectors.length} variant selectors`);

        // Listen for changes
        selectors.forEach((selector) => {
          selector.addEventListener('change', () => {
            console.log('🔄 Variant changed');
            setTimeout(() => this.updateModalImage(), 100);
          });
        });

        // Listen for any form changes as fallback
        const form = this.modalContent.querySelector('form');
        if (form) {
          form.addEventListener('change', () => {
            console.log('🔄 Form changed');
            setTimeout(() => this.updateModalImage(), 50);
          });
        }

        // Initial image update
        this.updateModalImage();

        console.log('✅ Modal image filtering setup complete');
      }

      // Update modal image based on selected variant
      updateModalImage() {
        console.log('🖼️ Updating modal image...');

        const modalImage = this.modalContent.querySelector('img');
        if (!modalImage) {
          console.log('❌ No modal image found');
          return;
        }

        // Try to get current variant directly from DOM
        const currentVariant = this.getCurrentVariantData();
        if (currentVariant) {
          console.log(`📊 Found variant: ${currentVariant.id}, Title: ${currentVariant.title}`);

          // Method 1: Try to use the variant's featured_media first
          if (currentVariant.featured_media && currentVariant.featured_media.preview_image) {
            console.log('🎯 Using variant featured media');
            const featuredImage = currentVariant.featured_media.preview_image;

            // Build the image URL (Shopify format)
            const imageUrl = featuredImage.src || featuredImage;
            modalImage.src = imageUrl;
            modalImage.alt = currentVariant.featured_media.alt || currentVariant.title;

            // Try to set srcset if available
            if (featuredImage.width) {
              const srcset = this.buildImageSrcset(imageUrl, featuredImage.width);
              modalImage.srcset = srcset;
            }

            modalImage.setAttribute('data-current-variant-id', currentVariant.id);
            console.log(`✅ Updated modal to featured media for variant ${currentVariant.id}`);
            return;
          }

          // Method 2: Fallback to model code matching
          let modelCode = null;
          if (currentVariant.option3) {
            modelCode = currentVariant.option3.toString();
            console.log(`📊 Model code from option3: ${modelCode}`);
          } else if (window.variantMetafields && window.variantMetafields[currentVariant.id]) {
            modelCode = window.variantMetafields[currentVariant.id].model_code.toString();
            console.log(`📊 Model code from metafields: ${modelCode}`);
          }

          if (modelCode && this.imageMap[modelCode]) {
            const imageData = this.imageMap[modelCode];

            modalImage.src = imageData.src;
            if (imageData.srcset) modalImage.srcset = imageData.srcset;
            if (imageData.alt) modalImage.alt = imageData.alt;
            modalImage.setAttribute('data-current-model-code', modelCode);

            console.log(`✅ Updated modal image to model code: ${modelCode}`);
          } else {
            console.log(`❌ No image available for model code: ${modelCode}`);
            console.log('Available model codes:', Object.keys(this.imageMap));
          }
          return;
        }

        // Fallback to old method
        const variantId = this.getCurrentVariantId();
        if (!variantId) {
          console.log('❌ No variant ID found');
          return;
        }

        const modelCode = this.getModelCodeForVariant(variantId);
        if (!modelCode) {
          console.log(`❌ No model code found for variant ${variantId}`);
          return;
        }

        console.log(`📊 Variant: ${variantId}, Model code: ${modelCode}`);

        if (this.imageMap[modelCode]) {
          const imageData = this.imageMap[modelCode];

          modalImage.src = imageData.src;
          if (imageData.srcset) modalImage.srcset = imageData.srcset;
          if (imageData.alt) modalImage.alt = imageData.alt;
          modalImage.setAttribute('data-current-model-code', modelCode);

          console.log(`✅ Updated modal image to model code: ${modelCode}`);
        } else {
          console.log(`❌ No image available for model code: ${modelCode}`);
        }
      }

      // NEW: Build responsive srcset for Shopify images
      buildImageSrcset(baseUrl, originalWidth) {
        const sizes = [165, 360, 533, 720, 940, 1066];
        const srcsetParts = [];

        sizes.forEach((size) => {
          if (size <= originalWidth) {
            const sizeUrl = baseUrl.includes('?')
              ? baseUrl.replace(/width=\d+/, `width=${size}`)
              : `${baseUrl}?width=${size}`;
            srcsetParts.push(`${sizeUrl} ${size}w`);
          }
        });

        // Add the original size
        srcsetParts.push(`${baseUrl} ${originalWidth}w`);

        return srcsetParts.join(', ');
      }

      // Get current variant data directly from DOM
      getCurrentVariantData() {
        try {
          // Method 1: From variant-selects
          const variantSelects = this.modalContent.querySelector('variant-selects');
          if (variantSelects) {
            const variantData = variantSelects.querySelector('[data-selected-variant]');
            if (variantData && variantData.innerHTML) {
              return JSON.parse(variantData.innerHTML);
            }
          }

          // Method 2: From product-info
          const productInfo = this.modalContent.querySelector('product-info');
          if (productInfo) {
            const variantData = productInfo.querySelector('[data-selected-variant]');
            if (variantData && variantData.innerHTML) {
              return JSON.parse(variantData.innerHTML);
            }
          }

          // Method 3: Try to find any element with variant data
          const allVariantData = this.modalContent.querySelectorAll('[data-selected-variant]');
          for (let element of allVariantData) {
            if (element.innerHTML) {
              try {
                return JSON.parse(element.innerHTML);
              } catch (e) {
                continue;
              }
            }
          }
        } catch (e) {
          console.log('Could not parse variant data:', e);
        }

        return null;
      }

      // Get current selected variant ID
      getCurrentVariantId() {
        // Method 1: Hidden input (most reliable)
        const variantInput = this.modalContent.querySelector('input[name="id"]');
        if (variantInput && variantInput.value) {
          return variantInput.value;
        }

        // Method 2: Look for data attributes
        const variantElement = this.modalContent.querySelector('[data-variant-id]');
        if (variantElement) {
          return variantElement.getAttribute('data-variant-id');
        }

        // Method 3: Try to construct from selected options
        return this.findVariantFromOptions();
      }

      // Find variant ID from selected options
      findVariantFromOptions() {
        if (!window.variantMetafields) return null;

        const selectedOptions = [];

        // Get values from select elements
        const selects = this.modalContent.querySelectorAll('select[name*="option"]');
        selects.forEach((select) => {
          if (select.value) selectedOptions.push(select.value);
        });

        // Get values from radio buttons
        const radios = this.modalContent.querySelectorAll('input[type="radio"]:checked');
        radios.forEach((radio) => {
          if (radio.value) selectedOptions.push(radio.value);
        });

        if (selectedOptions.length === 0) return null;

        // Find variant that matches all selected options
        for (const [variantId, variantData] of Object.entries(window.variantMetafields)) {
          const variantTitle = variantData.title || '';

          const allMatch = selectedOptions.every((option) => variantTitle.toLowerCase().includes(option.toLowerCase()));

          if (allMatch) {
            return variantId;
          }
        }

        return null;
      }

      // Get model code for variant
      getModelCodeForVariant(variantId) {
        // Method 1: Try variant metafields (if available)
        if (window.variantMetafields && window.variantMetafields[variantId]) {
          return window.variantMetafields[variantId].model_code.toString();
        }

        // Method 2: Extract from variant option3 (fallback)
        const variantData = this.getVariantData(variantId);
        if (variantData && variantData.option3) {
          return variantData.option3.toString();
        }

        return null;
      }

      // Get variant data from DOM
      getVariantData(variantId) {
        try {
          // Check if there's variant data in the modal
          const variantSelects = this.modalContent.querySelector('variant-selects');
          if (variantSelects) {
            const variantData = variantSelects.querySelector('[data-selected-variant]');
            if (variantData && variantData.innerHTML) {
              const variant = JSON.parse(variantData.innerHTML);
              if (variant.id.toString() === variantId.toString()) {
                return variant;
              }
            }
          }

          // Try to find variant data in product-info
          const productInfo = this.modalContent.querySelector('product-info');
          if (productInfo) {
            const variantData = productInfo.querySelector('[data-selected-variant]');
            if (variantData && variantData.innerHTML) {
              const variant = JSON.parse(variantData.innerHTML);
              if (variant.id.toString() === variantId.toString()) {
                return variant;
              }
            }
          }
        } catch (e) {
          console.log('Could not parse variant data:', e);
        }

        return null;
      }

      // Original Dawn preprocessing functions
      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}

// Manual testing function
window.testModalImageUpdate = function (modelCode) {
  const modal = document.querySelector('quick-add-modal');
  if (!modal) {
    console.log('❌ No modal found');
    return false;
  }

  if (!modal.imageMap || !modal.imageMap[modelCode]) {
    console.log(`❌ No image data for model code: ${modelCode}`);
    console.log('Available model codes:', Object.keys(modal.imageMap || {}));
    return false;
  }

  const modalImage = modal.modalContent?.querySelector('img');
  if (!modalImage) {
    console.log('❌ No modal image found');
    return false;
  }

  const imageData = modal.imageMap[modelCode];
  modalImage.src = imageData.src;
  if (imageData.srcset) modalImage.srcset = imageData.srcset;
  if (imageData.alt) modalImage.alt = imageData.alt;

  console.log(`✅ Manually updated modal to model code: ${modelCode}`);
  return true;
};
